import { useState, useEffect } from 'react';
import './PermissionGate.css';

const PermissionGate = ({ onPermissionsGranted }) => {
  const [cameraPermission, setCameraPermission] = useState('pending');
  const [micPermission, setMicPermission] = useState('pending');
  const [error, setError] = useState('');
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS device
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
  }, []);

  const requestPermissions = async () => {
    setError('');
    
    try {
      // For iOS, we need to request permissions separately and handle them differently
      if (isIOS) {
        // Request camera with specific iOS constraints
        const cameraStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Prefer back camera
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        setCameraPermission('granted');
        cameraStream.getTracks().forEach(track => track.stop());

        // Request microphone separately for iOS
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        setMicPermission('granted');
        micStream.getTracks().forEach(track => track.stop());
      } else {
        // Non-iOS devices can request both simultaneously
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: true, 
          audio: true 
        });
        setCameraPermission('granted');
        setMicPermission('granted');
        stream.getTracks().forEach(track => track.stop());
      }

      onPermissionsGranted();
    } catch (err) {
      console.error('Permission error:', err);
      
      // Provide more specific error messages for iOS users
      if (isIOS) {
        setError(
          'On iOS devices, please ensure:\n' +
          '1. You\'re using Safari browser\n' +
          '2. Camera and microphone permissions are enabled in Settings\n' +
          '3. You\'re on a secure (HTTPS) connection'
        );
      } else {
        setError('Please allow both camera and microphone access to use this app.');
      }
      
      setCameraPermission('denied');
      setMicPermission('denied');
    }
  };

  return (
    <div className="permission-gate">
      <div className="permission-container">
        <h1>Welcome to Blind Assistant</h1>
        <p className="permission-text">
          {isIOS 
            ? "This app requires camera and microphone access. Please use Safari browser for the best experience."
            : "This app requires camera and microphone access to help describe your surroundings."}
        </p>
        
        <div className="permission-status">
          <div className={`status-item ${cameraPermission}`}>
            <span className="status-icon">📷</span>
            <span>Camera: {cameraPermission}</span>
          </div>
          <div className={`status-item ${micPermission}`}>
            <span className="status-icon">🎤</span>
            <span>Microphone: {micPermission}</span>
          </div>
        </div>

        {error && <p className="error-message">{error.split('\n').map((line, i) => (
          <span key={i}>
            {line}
            <br />
          </span>
        ))}</p>}
        
        <button 
          className="permission-button"
          onClick={requestPermissions}
          disabled={cameraPermission === 'granted' && micPermission === 'granted'}
        >
          {cameraPermission === 'granted' && micPermission === 'granted'
            ? 'Permissions Granted!'
            : 'Allow Access'}
        </button>

        {isIOS && (
          <p className="ios-note">
            Note: If permissions don't work, please check your iOS Settings → Safari → Camera & Microphone permissions
          </p>
        )}
      </div>
    </div>
  );
};

export default PermissionGate; 